import React, { useEffect, useRef, useState } from 'react';
import './UpdateWindow.css';
// Environment detection
const isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'dev';

const HIGHLIGHT_RED = '#e74c3c';

const Spinner = () => (
  <span style={{
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    pointerEvents: 'none',
    width: 32,
    height: 32,
  display: 'flex',
  alignItems: 'center',
    justifyContent: 'center',
  }}>
    <svg viewBox="0 0 50 50" style={{ width: 32, height: 32, display: 'block' }}>
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="#1976d2"
        strokeWidth="6"
        strokeDasharray="31.4 31.4"
        strokeLinecap="round"
        transform="rotate(-90 25 25)"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 25 25"
          to="360 25 25"
          dur="0.7s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  </span>
);

const UpdateWindow = () => {
  const [status, setStatus] = useState('Ready to check for updates.');
  const [currentVersion, setCurrentVersion] = useState('');
  const [progress, setProgress] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [releaseHistory, setReleaseHistory] = useState([]);
  const [installReady, setInstallReady] = useState(false);
  const [showNewUpdateMsg, setShowNewUpdateMsg] = useState(false);
  const lastProgressRef = useRef(0);
  const releaseHistoryRef = useRef([]);

  useEffect(() => {
    releaseHistoryRef.current = releaseHistory;
  }, [releaseHistory]);

  useEffect(() => {
    console.log('[UpdateWindow] Component mounted/updated');

    if (!window.electronAPI) {
      console.log('[UpdateWindow] electronAPI not available');
      // Show fallback data for testing
      setCurrentVersion('0.1.0');
      setStatus('electronAPI not available - running in web mode');
      return;
    }
    
    // Add error boundary for the component
    const handleError = (error) => {
      console.error('[UpdateWindow] Component error:', error);
      setStatus('Component error: ' + error.message);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    
    console.log('[UpdateWindow] Setting up update window...');
    console.log('[UpdateWindow] Environment:', isDev ? 'Development (MinIO)' : 'Production (R2)');
    
    // Get current version
    window.electronAPI.getVersion?.().then(version => {
      console.log('[UpdateWindow] Current version:', version);
      setCurrentVersion(version);
      // Detect if opened due to new update (latest > current)
      setTimeout(() => {
        if (releaseHistoryRef.current.length > 0 && isNewerVersion(releaseHistoryRef.current[0].version, version)) {
          setShowNewUpdateMsg(true);
        }
      }, 500);
    }).catch(err => {
      console.error('[UpdateWindow] Error getting version:', err);
      setCurrentVersion('0.1.0'); // Fallback version
    });
    
    // Set up update status listener
    const handleUpdateStatus = (data) => {
      console.log('[UpdateWindow] ===== RECEIVED UPDATE STATUS =====');
      console.log('[UpdateWindow] Received update status:', data);
      console.log('[UpdateWindow] Data type:', data.type, 'Message:', data.message);
      console.log('[UpdateWindow] =================================');
      
      if (data.currentVersion) {
        setCurrentVersion(data.currentVersion);
      }
      
      if (data.latestVersion) {
        setIsChecking(false);
      } else if (data.type === 'info' && data.message.includes('No updates available')) {
        setStatus('You are on the latest version.');
        setIsChecking(false);
      } else if (data.type === 'error') {
        setStatus(data.message);
        setIsChecking(false);
        setIsDownloading(false);
      } else if (data.type === 'info' && data.message === 'Checking for updates...') {
        setStatus('Checking for updates...');
        setIsChecking(true);
      } else if (data.type === 'info' && data.message === 'Downloading update...') {
        setStatus('Downloading update...');
        setIsDownloading(true);
        setIsChecking(false);
        setInstallReady(false);
        // Reset progress tracking for new download
        lastProgressRef.current = 0;
      } else if (data.type === 'success' && data.message.includes('downloaded successfully')) {
        setStatus('Update downloaded! Click Install and Restart.');
        setProgress(100);
        setIsDownloading(false);
        setInstallReady(true);
      } else if (data.type === 'info' && data.message.includes('Installing update')) {
        setStatus('Installing update and restarting...');
        setIsDownloading(false);
        setInstallReady(false);
      } else if (data.type === 'success' && data.message.includes('Update available')) {
        console.log('[UpdateWindow] Update available:', data.message);
        setStatus(data.message);
        setIsChecking(false);
        setInstallReady(false);
      }
      
      if (data.progress) {
        const newProgress = Math.round(data.progress.percent);
        // Progress persistence: Only update if moving forward or at 100%
        // This prevents progress resets during network issues or retries
        if (newProgress >= lastProgressRef.current || newProgress === 100) {
          setProgress(newProgress);
          lastProgressRef.current = newProgress;
        }
      }
    };

    if (window.electronAPI && window.electronAPI.onUpdateStatus) {
      console.log('[UpdateWindow] ===== SETTING UP IPC LISTENER =====');
      console.log('[UpdateWindow] Setting up onUpdateStatus listener');
      console.log('[UpdateWindow] electronAPI.onUpdateStatus type:', typeof window.electronAPI.onUpdateStatus);
      window.electronAPI.onUpdateStatus(handleUpdateStatus);
      console.log('[UpdateWindow] onUpdateStatus listener set up successfully');
      console.log('[UpdateWindow] ===================================');
    } else {
      console.log('[UpdateWindow] ===== IPC LISTENER NOT AVAILABLE =====');
      console.log('[UpdateWindow] onUpdateStatus not available in electronAPI');
      console.log('[UpdateWindow] electronAPI keys:', Object.keys(window.electronAPI || {}));
      console.log('[UpdateWindow] ======================================');
    }
    
    // Don't call checkForUpdates() automatically - the main process handles this
    // when the update window is opened. The main process will send IPC messages
    // to this renderer process with the update status.
    console.log('[UpdateWindow] Waiting for update status from main process...');
    
    // Test IPC communication
    setTimeout(() => {
      console.log('[UpdateWindow] Testing IPC communication...');
      if (window.electronAPI && window.electronAPI.send) {
        window.electronAPI.send('test-update-window-ready', { 
          ready: true, 
          timestamp: Date.now() 
        });
        console.log('[UpdateWindow] Test IPC message sent');
      } else {
        console.log('[UpdateWindow] No send method available');
      }
    }, 1000);

    // Fetch release history
    const fetchReleaseHistory = async () => {
      if (!window.electronAPI?.getReleaseHistory) {
        console.log('[UpdateWindow] getReleaseHistory not available');
        return;
      }
      try {
        const result = await window.electronAPI.getReleaseHistory();
        if (result.success) {
          setReleaseHistory(result.releases || []);
          setSelectedIndex(0);
        } else {
          console.error('[UpdateWindow] Failed to load release history:', result.error);
        }
      } catch (error) {
        console.error('[UpdateWindow] Error fetching release history:', error);
      }
    };

    fetchReleaseHistory();

    return () => {
      console.log('[UpdateWindow] Component unmounting/cleanup');
      if (window.electronAPI) {
        window.electronAPI.removeListener?.('update-status', handleUpdateStatus);
      }
      // Remove error listeners
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  const handleInstallUpdate = () => {
    console.log('[UpdateWindow] Install and Restart button clicked');
    setStatus('Installing update and restarting...');
    setIsDownloading(false);
    setInstallReady(false);
    window.electronAPI.installUpdate?.();
  };

  const isNewerVersion = (latest, current) => {
    if (!latest || !current) return false;
    const l = latest.split('.').map(Number);
    const c = current.split('.').map(Number);
    for (let i = 0; i < Math.max(l.length, c.length); i++) {
      if ((l[i] || 0) > (c[i] || 0)) return true;
      if ((l[i] || 0) < (c[i] || 0)) return false;
    }
    return false;
  };

  const handleVersionUpdate = (versionObj) => {
    console.log('[UpdateWindow] Upgrade/Downgrade button clicked for version:', versionObj);
    setStatus(`Preparing to update to version ${versionObj.version}...`);
    setIsDownloading(true);
    setInstallReady(false);
    setProgress(0);
    window.electronAPI.downloadVersionUpdate(versionObj).then((res) => {
      console.log('[UpdateWindow] downloadVersionUpdate IPC response:', res);
    });
  };

  // Show the 15 most recent releases
  const recentReleases = releaseHistory.slice(0, 15);

  return (
    <div className="update-window">
      {/* Header */}
      <div className="update-header">
        <div className="update-title">
          <svg className="update-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          App Updates
        </div>
        <div className="window-controls-placeholder" />
      </div>

      {/* Current Version Info */}
      <div className="version-info">
        Current version: <span className="version-number">{currentVersion}</span>
      </div>

      {/* Main Content */}
      <div className="update-content">
        {/* Releases List */}
        <div className="releases-panel">
          {recentReleases.length > 0 ? (
            recentReleases.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              const isCurrent = item.version === currentVersion;
              const isLatest = idx === 0 && isNewerVersion(item.version, currentVersion);
            return (
              <button
                key={idx}
                  className={`release-item${isSelected ? ' selected' : ''}${isCurrent ? ' current-version' : ''}`}
                onClick={() => setSelectedIndex(idx)}
                  style={isLatest ? { borderLeft: `6px solid ${HIGHLIGHT_RED}` } : {}}
              >
                  <div className="release-version">{item.version}</div>
                  {item.date && (
                    <div className="release-date">{new Date(item.date).toLocaleDateString()}</div>
                  )}
                  {isCurrent && (
                    <div style={{ position: 'absolute', right: 10, top: 10, color: '#3498b7', fontSize: 11, fontWeight: 700 }}>Current</div>
                  )}
              </button>
            );
            })
          ) : (
            <div className="no-releases">
              {isChecking ? 'Checking for updates...' : 'No releases found'}
            </div>
          )}
        </div>

        {/* Release Details */}
        <div className="release-details">
          <div className="release-notes-panel">
            <h3>Release Notes</h3>
            {releaseHistory[selectedIndex] ? (
              <div className="release-notes" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {releaseHistory[selectedIndex].note || 'No release notes available.'}
              </div>
            ) : (
              <div className="no-notes">No release notes available.</div>
            )}
          </div>

          {/* Status and Progress */}
          <div className="update-status">
            {showNewUpdateMsg && (
              <div className="status-message" style={{ color: HIGHLIGHT_RED, fontWeight: 700 }}>
                New update available!
          </div>
            )}
            {/* Show status only for errors, downloads, or install states */}
            {(isDownloading || installReady || status.startsWith('Error') || status.startsWith('Downloading') || status.startsWith('Installing')) ? (
              <div className="status-message">{status}</div>
            ) : null}
            {/* Show 'You are on the latest version.' only if selected is current and no upgrades available */}
            {recentReleases[selectedIndex] && recentReleases[selectedIndex].version === currentVersion && !recentReleases.some(r => isNewerVersion(r.version, currentVersion)) && !isDownloading && !installReady && !showNewUpdateMsg ? (
              <div className="status-message">You are on the latest version.</div>
            ) : null}
            {/* Progress bar */}
            {(isDownloading || progress > 0) && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="progress-text">{progress}%</div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {recentReleases[selectedIndex] && isNewerVersion(recentReleases[selectedIndex].version, currentVersion) ? (
              installReady ? (
                <button
                  className="btn btn-success"
                  onClick={handleInstallUpdate}
                  disabled={isDownloading}
                  style={{ position: 'relative', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', display: 'flex', overflow: 'hidden' }}
                >
                  {isDownloading && <Spinner />}
                  <span style={{ opacity: isDownloading ? 0.5 : 1, zIndex: 1 }}>
                    Install and Restart
                  </span>
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => handleVersionUpdate(recentReleases[selectedIndex])}
                  disabled={isDownloading}
                  style={{ position: 'relative', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', display: 'flex', overflow: 'hidden' }}
                >
                  {isDownloading && <Spinner />}
                  <span style={{ opacity: isDownloading ? 0.5 : 1, zIndex: 1 }}>
                    Upgrade to {recentReleases[selectedIndex].version}
                  </span>
                </button>
              )
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateWindow; 